/**
 * Bundled Icons Configuration
 * 
 * This file registers all icons used in the app at build time,
 * eliminating the need to fetch them from the Iconify API on every page load.
 * 
 * To add a new icon:
 * 1. Import it from the appropriate @iconify/icons-* package
 * 2. Add it to the icons object with the full icon name as the key
 * 
 * For icons from packages not available on npm (like bitcoin-icons, circum, hugeicons),
 * we define them manually using their SVG paths.
 */
import { addIcon } from 'iconify-icon';

// heroicons-outline
import home from '@iconify/icons-heroicons-outline/home';
import users from '@iconify/icons-heroicons-outline/users';
import banknotes from '@iconify/icons-heroicons-outline/banknotes';
import cog6Tooth from '@iconify/icons-heroicons-outline/cog-6-tooth';
import chatBubbleLeftRight from '@iconify/icons-heroicons-outline/chat-bubble-left-right';
import arrowRightCircle from '@iconify/icons-heroicons-outline/arrow-right-circle';
import pencilSquare from '@iconify/icons-heroicons-outline/pencil-square';
import lockClosed from '@iconify/icons-heroicons-outline/lock-closed';
import userCircle from '@iconify/icons-heroicons-outline/user-circle';
import microphone from '@iconify/icons-heroicons-outline/microphone';

// heroicons-solid
import chatBubbleOvalLeftEllipsis from '@iconify/icons-heroicons-solid/chat-bubble-oval-left-ellipsis';
import arrowsRightLeft from '@iconify/icons-heroicons-solid/arrows-right-left';
import checkCircleSolid from '@iconify/icons-heroicons-solid/check-circle';

// heroicons (20-solid style)
import heroMicrophone from '@iconify/icons-heroicons/microphone';
import heroStop from '@iconify/icons-heroicons/stop';
import heroCheckCircle from '@iconify/icons-heroicons/check-circle';
import heroExclamationTriangle from '@iconify/icons-heroicons/exclamation-triangle';
import heroXCircle from '@iconify/icons-heroicons/x-circle';
import heroArrowPath from '@iconify/icons-heroicons/arrow-path';

// mdi
import clockOutline from '@iconify/icons-mdi/clock-outline';
import menu from '@iconify/icons-mdi/menu';
import check from '@iconify/icons-mdi/check';
import informationOutline from '@iconify/icons-mdi/information-outline';
import chevronUp from '@iconify/icons-mdi/chevron-up';
import chevronDown from '@iconify/icons-mdi/chevron-down';

// svg-spinners (animated)
import ring180 from '@iconify/icons-svg-spinners/180-ring';

// Custom icons not available in npm packages - defined manually
// These are defined inline since their npm packages don't exist

const customIcons = {
  // bitcoin-icons:cross-outline
  'bitcoin-icons:cross-outline': {
    body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 6l12 12M6 18L18 6"/>',
    width: 24,
    height: 24
  },
  // circum:logout  
  'circum:logout': {
    body: '<path fill="currentColor" d="M14 3.5h5.5a.5.5 0 0 1 .5.5v16a.5.5 0 0 1-.5.5H14a.5.5 0 0 0 0 1h5.5a1.5 1.5 0 0 0 1.5-1.5V4a1.5 1.5 0 0 0-1.5-1.5H14a.5.5 0 0 0 0 1Z"/><path fill="currentColor" d="M15.854 12.354a.5.5 0 0 0 0-.708l-4.5-4.5a.5.5 0 1 0-.708.708L14.293 11.5H4a.5.5 0 0 0 0 1h10.293l-3.647 3.646a.5.5 0 0 0 .708.708l4.5-4.5Z"/>',
    width: 24,
    height: 24
  },
  // hugeicons:upload-01
  'hugeicons:upload-01': {
    body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 14.5v-10m0 0l3.5 3.5M12 4.5L8.5 8M4.5 17v.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V17"/>',
    width: 24,
    height: 24
  },
  // hugeicons:delete-01
  'hugeicons:delete-01': {
    body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.5 5.5L18.613 18.227c-.111 1.568-.166 2.352-.5 2.944a3 3 0 0 1-1.298 1.202c-.617.327-1.403.327-2.974.327h-3.682c-1.571 0-2.357 0-2.974-.327a3 3 0 0 1-1.298-1.202c-.334-.592-.389-1.376-.5-2.944L4.5 5.5m3 0V4.7c0-.84 0-1.26.163-1.581a1.5 1.5 0 0 1 .656-.656c.32-.163.74-.163 1.581-.163h4.2c.84 0 1.26 0 1.581.163a1.5 1.5 0 0 1 .656.656c.163.32.163.74.163 1.581v.8M3 5.5h18M14 10v6m-4-6v6"/>',
    width: 24,
    height: 24
  },
  // hugeicons:message-favourite-01
  'hugeicons:message-favourite-01': {
    body: '<path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12c0 1.619.384 3.151 1.066 4.508c.159.316.13.699-.019 1.023l-.74 1.607c-.319.694-.479 1.041-.392 1.297a.75.75 0 0 0 .43.43c.256.088.603-.073 1.297-.392l1.607-.74c.324-.149.707-.178 1.023-.019A9.96 9.96 0 0 0 12 22Z"/><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15.5s-3.5-1.969-3.5-4.046c0-1.538 1.22-2.454 2.5-2.454c.8 0 1 .5 1 .5s.2-.5 1-.5c1.28 0 2.5.916 2.5 2.454C15.5 13.53 12 15.5 12 15.5Z"/>',
    width: 24,
    height: 24
  }
};

/**
 * Registers all icons used in the application.
 * Call this function during app initialization.
 */
export function registerBundledIcons(): void {
  // heroicons-outline
  addIcon('heroicons-outline:home', home);
  addIcon('heroicons-outline:users', users);
  addIcon('heroicons-outline:banknotes', banknotes);
  addIcon('heroicons-outline:cog-6-tooth', cog6Tooth);
  addIcon('heroicons-outline:chat-bubble-left-right', chatBubbleLeftRight);
  addIcon('heroicons-outline:arrow-right-circle', arrowRightCircle);
  addIcon('heroicons-outline:pencil-square', pencilSquare);
  addIcon('heroicons-outline:lock-closed', lockClosed);
  addIcon('heroicons-outline:user-circle', userCircle);
  addIcon('heroicons-outline:microphone', microphone);

  // heroicons-solid
  addIcon('heroicons-solid:chat-bubble-oval-left-ellipsis', chatBubbleOvalLeftEllipsis);
  addIcon('heroicons-solid:arrows-right-left', arrowsRightLeft);
  addIcon('heroicons-solid:check-circle', checkCircleSolid);

  // heroicons (20-solid)
  addIcon('heroicons:microphone', heroMicrophone);
  addIcon('heroicons:stop', heroStop);
  addIcon('heroicons:check-circle', heroCheckCircle);
  addIcon('heroicons:exclamation-triangle', heroExclamationTriangle);
  addIcon('heroicons:x-circle', heroXCircle);
  addIcon('heroicons:arrow-path', heroArrowPath);

  // mdi
  addIcon('mdi:clock-outline', clockOutline);
  addIcon('mdi:menu', menu);
  addIcon('mdi:check', check);
  addIcon('mdi:information-outline', informationOutline);
  addIcon('mdi:chevron-up', chevronUp);
  addIcon('mdi:chevron-down', chevronDown);

  // svg-spinners
  addIcon('svg-spinners:180-ring', ring180);

  // Custom icons (not available in npm packages)
  for (const [name, data] of Object.entries(customIcons)) {
    addIcon(name, data);
  }
}
